import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import JBImage from 'components/essentials/JBImage';
import { GET_VIDEO_METADATA } from 'mocks/queries';
import StatusLabel from 'components/consumer/StatusLabel';
import RatioContainer from 'components/consumer/RatioContainer';
import LivestreamPlayIcon from '../LivestreamPlayIcon';

const LivestreamVideoThumbnail = ({
  slug,
  category,
  image,
  isSelected,
  isRecordedLive,
  title,
  videoLength,
  videoType,
  videoId,
}) => {
  const [duration, updateDuration] = useState(videoLength);
  const [thumbnail, updateThumbnail] = useState(image);
  const { loading, data } = useSuspenseQuery(GET_VIDEO_METADATA, {
    variables: { id: videoId, type: videoType },
  });
  const durationToTime = totalSeconds => {
    if (totalSeconds < 86400) {
      const result = new Date(Math.ceil(totalSeconds) * 1000)
        .toISOString()
        .substr(11, 8);
      return result.replace(/^0(?:0:0?)?/, '');
    }
    return totalSeconds;
  };
  useEffect(() => {
    if (!loading && data?.getVideoMetadata) {
      if (data.getVideoMetadata?.duration)
        updateDuration(data.getVideoMetadata.duration);

      if (data.getVideoMetadata?.thumbnails.length > 0) {
        const url =
          data.getVideoMetadata.thumbnails.length > 3
            ? data.getVideoMetadata.thumbnails[3]
            : data.getVideoMetadata.thumbnails[0];
        updateThumbnail(url);
      }
    }
  }, [loading, data?.getVideoMetadata]);

  return (
    <Link
      className="md:pb-4 hover:cursor-pointer"
      to={{
        pathname: `/videos/${slug}/`,
        state: { scrollToTop: false },
      }}
    >
      <RatioContainer ratio={9 / 16}>
        {thumbnail && (
          <JBImage
            src={thumbnail}
            width={300}
            className="h-full absolute top-0 left-0 w-full"
          />
        )}

        {isRecordedLive && (
          <StatusLabel
            className="absolute right-[15px] top-[15px] md:right-5 md:top-5"
            color="red-dark"
          >
            RECORDED LIVE
          </StatusLabel>
        )}

        {!!duration && (
          <div className="text-sm font-bold bottom-5 text-white absolute right-5 [text-shadow:0_0_10px_#000]">
            {durationToTime(duration)}
          </div>
        )}

        <div
          className={classNames(
            'bottom-0 left-0 absolute right-0 top-0 [transition:opacity_.1s]',
            {
              'opacity-0': !isSelected,
              'opacity-100': isSelected,
            }
          )}
        >
          <div className="bg-black bottom-0 left-0 opacity-60 absolute right-0 top-0" />
          <div className="text-xs font-bold text-white items-center bottom-0 flex justify-center left-0 absolute right-0 top-0">
            NOW PLAYING
          </div>
        </div>

        <div
          className={classNames(
            'items-center bottom-0 flex justify-center left-0 opacity-0 absolute right-0 top-0 [transition:opacity_.15s]',
            {
              'hover:opacity-100': !isSelected,
              'hover:opacity-0': isSelected,
            }
          )}
        >
          <LivestreamPlayIcon />
        </div>
      </RatioContainer>

      <div className="text-lg font-bold text-gray mt-4">{title}</div>
      <div className="text-xs text-gray-light1 mt-1">{category}</div>
    </Link>
  );
};

LivestreamVideoThumbnail.propTypes = {
  category: PropTypes.string,
  image: PropTypes.string.isRequired,
  isRecordedLive: PropTypes.bool,
  isSelected: PropTypes.bool,
  title: PropTypes.string.isRequired,
  videoLength: PropTypes.string,
  videoType: PropTypes.string,
  videoId: PropTypes.string,
  slug: PropTypes.string,
};

export default LivestreamVideoThumbnail;
